// src/components/sections/TextSection.js
import React, { useEffect, useRef } from 'react';

const TextSection = ({ content }) => {
  const sectionRef = useRef(null);
  
  const { 
    title, 
    text, 
    alignment = 'left',
    fontFamily = 'font-sans',
    fontSize = 'text-base',
    textColor = 'text-gray-900',
    bgColor = '',
    customTextColor,
    customBgColor,
    typography = '',
    lineHeight = 'leading-normal',
    containerStyle = '',
    animation = '',
    letterSpacing = 'tracking-normal',
    textTransform = '',
    columnCount = '1',
    textShadow = '',
    marginTop = '4',
    marginBottom = '4',
  } = content;

  // Translate alignment to Tailwind classes
  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };

  // Create container styles based on the selected style
  let containerClasses = '';
  if (containerStyle === 'card') containerClasses = 'shadow-md rounded-lg p-6';
  if (containerStyle === 'bordered') containerClasses = 'border border-gray-300 rounded p-4';
  if (containerStyle === 'callout') containerClasses = 'border-l-4 border-blue-500 pl-4 py-2';
  if (containerStyle === 'quote') containerClasses = 'border-l-4 border-gray-300 italic pl-4 py-2';
  if (containerStyle === 'highlight') containerClasses = 'bg-yellow-50 p-4 rounded';

  // Column classes
  const columnClasses = {
    '1': '',
    '2': 'sm:columns-2 gap-8',
    '3': 'sm:columns-3 gap-8',
    '4': 'sm:columns-4 gap-8'
  };

  // Text shadow classes
  const shadowClasses = {
    '': '',
    'text-shadow-sm': 'shadow-sm',
    'text-shadow': 'shadow',
    'text-shadow-lg': 'shadow-lg'
  };

  // Animation handling
  useEffect(() => {
    if (!animation || !sectionRef.current) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          
          // Add animation class based on the selected animation
          switch(animation) {
            case 'fade-in':
              element.classList.add('animate-fade-in');
              break;
            case 'slide-up':
              element.classList.add('animate-slide-up');
              break;
            case 'slide-in-left':
              element.classList.add('animate-slide-in-left');
              break;
            case 'slide-in-right':
              element.classList.add('animate-slide-in-right');
              break;
            case 'zoom-in':
              element.classList.add('animate-zoom-in');
              break;
            case 'typing':
              // If it's a typing animation, we need special handling
              if (element.querySelector('.typing-effect')) {
                element.querySelector('.typing-effect').classList.add('animate-typing');
              }
              break;
            default:
              break;
          }
          
          // Stop observing after animation is triggered
          observer.unobserve(element);
        }
      });
    }, { threshold: 0.1 });
    
    observer.observe(sectionRef.current);
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [animation]);

  // Dynamic inline styles for custom colors
  const sectionStyle = {};
  if (textColor === 'custom' && customTextColor) {
    sectionStyle.color = customTextColor;
  }
  if (bgColor === 'custom' && customBgColor) {
    sectionStyle.backgroundColor = customBgColor;
  }

  // Construct comprehensive className
  const textClasses = `
    ${alignmentClasses[alignment]} 
    ${fontFamily} 
    ${fontSize} 
    ${textColor !== 'custom' ? textColor : ''} 
    ${typography} 
    ${lineHeight} 
    ${letterSpacing} 
    ${textTransform} 
    ${columnCount !== '1' ? columnClasses[columnCount] : ''} 
    ${textShadow ? shadowClasses[textShadow] : ''}
    mt-${marginTop} mb-${marginBottom}
  `;

  // For typing effect animation
  const renderText = () => {
    if (animation === 'typing' && text) {
      return <p className={`typing-effect text-lg overflow-hidden whitespace-nowrap ${textClasses}`}>{text}</p>;
    }
    return <p className={`text-lg ${textClasses}`}>{text}</p>;
  };

  return (
    <div 
      ref={sectionRef}
      className={`py-6 ${bgColor !== 'custom' ? bgColor : ''} ${containerClasses}`}
      style={sectionStyle}
    >
      <div className={animation === 'typing' ? '' : textClasses}>
        {title && <h2 className={`text-3xl font-bold mb-6 ${animation === 'typing' ? textClasses : ''}`}>{title}</h2>}
        {text && renderText()}
      </div>

      <style>
        {`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        @keyframes slideInLeft {
          from { transform: translateX(-100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes slideInRight {
          from { transform: translateX(100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes zoomIn {
          from { transform: scale(0.5); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        @keyframes typing {
          from { width: 0 }
          to { width: 100% }
        }

        .animate-fade-in {
          animation: fadeIn 1s ease-out forwards;
        }

        .animate-slide-up {
          animation: slideUp 0.8s ease-out forwards;
        }

        .animate-slide-in-left {
          animation: slideInLeft 0.8s ease-out forwards;
        }

        .animate-slide-in-right {
          animation: slideInRight 0.8s ease-out forwards;
        }

        .animate-zoom-in {
          animation: zoomIn 0.8s ease-out forwards;
        }

        .animate-typing {
          animation: typing 3.5s steps(40, end) forwards;
          border-right: .15em solid orange;
          white-space: nowrap;
          overflow: hidden;
          width: 0;
        }

        /* Column gap styling */
        .gap-8 {
          column-gap: 2rem;
        }

        /* Text shadow utilities */
        .text-shadow-sm {
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
        }

        .text-shadow {
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
        }

        .text-shadow-lg {
          text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.3);
        }

        /* Font families */
        .font-display {
          font-family: 'Impact', 'Arial Black', sans-serif;
        }

        .font-handwriting {
          font-family: 'Brush Script MT', cursive;
        }
        `}
      </style>
    </div>
  );
};

export default TextSection;



